import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Grid,
  Snackbar,
  Alert,
  AlertTitle,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import logo from "../src/assets/openIMIS.png";
import Hidden from "@mui/material/Hidden";
import Tooltip from "@mui/material/Tooltip";
import Sidebar from "../src/reusableComponents/components/generics/Sidebar";
import LogoutIcon from "@mui/icons-material/Logout";
import LocalizationDropdown from "../src/reusableComponents/components/generics/LocalizationDropdown";
import { useTranslation } from "react-i18next";
import {
  HOME_PAGE,
  idleTimeSet,
  POLICY_HOLDER,
  PORTAL,
  HAS_PERMISSION_TO_VIEW_PAGE,
  PATH_NAME_PERMISSIONS,
} from "./utils/constant";
import { useSelector, useDispatch } from "react-redux";
import { useIdleTimer } from "react-idle-timer";
import NotificationDialog from "./components/dialog/NotificationDialog";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import { fetchNotifications } from "./action";
import { useLocation } from "react-router-dom";
import { useConvention } from "./hooks/useConvention";

export function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const HeaderAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== "isSidebarOpen",
})(({ theme, isSidebarOpen, policyholderPage }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: isSidebarOpen ? "250px" : "0",
  width: isSidebarOpen ? `calc(100% - 290px)` : "100%",
  background: policyholderPage ? "#00913E" : "white",
}));

const CustomToolbar = styled(Toolbar)(({ theme, isSidebarOpen }) => ({
  display: "flex",
}));

const Content = styled("div")({
  display: "flex",
});

const SidebarContainer = styled("div")(({ isSidebarOpen }) => ({
  width: isSidebarOpen ? "290px" : "0",
  overflowX: "hidden",
  transition: "width 0.3s",
}));

const MainContent = styled("div")(({ theme, isSidebarOpen }) => ({
  flexGrow: 1,
  padding: "20px 0px",
  marginTop: "5rem",
  marginLeft: "1rem",
  maxWidth: isSidebarOpen ? `calc(100% - 290px)` : "100%",
  width: isSidebarOpen ? `calc(100% - 250px)` : "80%",
  overflowY: "auto",
}));

const NotificationContent = styled("div")(({ theme, isSidebarOpen }) => ({
  position: "relative",
  cursor: "pointer",
  margin: "3px 9px 0 0",
  color: "grey",
}));

const NotificationCount = styled("div")(({ theme, isSidebarOpen }) => ({
  position: "absolute",
  top: "5",
  right: "0",
  width: "15px",
  height: "15px",
  background: "red",
  borderRadius: "50%",
  marginBottom: "20px",
  textAlign: "center",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  color: "#fff",
}));

const StyledAlertTitle = styled(AlertTitle)(({}) => ({
  fontWeight: "bold",
  color: "red",
}));

const Header = ({ children, handleChange, policyholderPage }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const currentUserRoles = useSelector(
    (state) => state.profileReducer.currentUserRoles?.roles
  );
  const [language, setLanguage] = useState(
    localStorage.getItem("selectedLanguage") || "fr"
  );
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const query = useQuery();

  const userData = useSelector((state) => state.profileReducer);
  const dispatch = useDispatch();
  const handleLanguageChange = (event) => {
    i18n.changeLanguage(event.target.value);
    setLanguage(event.target.value);
    localStorage.setItem("selectedLanguage", event.target.value);
  };

  const { isConventionnementActive, blockageReason } = useConvention();

  const hideMenu = query.get("hideMenuNavigation");

  useEffect(() => {
    const storedLanguage = localStorage.getItem("selectedLanguage");
    if (storedLanguage) {
      i18n.changeLanguage(storedLanguage);
    }
  }, []);
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  const userId = userData?.currentUserResponseData?.id;
  useEffect(() => {
    if (policyholderPage) {
      setIsSidebarOpen(false);
    }
    if (!!userId) {
      dispatch(fetchNotifications(userId));
    }
  }, [policyholderPage, userId]);

  const formatters = [
    (i) => !!i.documentName && i.documentName,
    (i) => !!i.documentId && i.documentId,
  ];
  const documentDetails = { documentName: "ffej", documentId: 232 };
  const rowsPerPageOptions = [5, 10];
  const LogoutHandler = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/graphql`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            query: `
            mutation {
              deleteTokenCookie {
                deleted
              }
              deleteRefreshTokenCookie {
                deleted
              }
            }
          `,
          }),
        }
      );
      if (!response.ok) {
        throw new Error("Failed to logout");
      }
      const responseData = await response.json();
      if (responseData.errors) {
        throw new Error("Failed to delete cookies");
      }
      if (openSnackbar) {
        setTimeout(() => {
          window.location.href = PORTAL;
          localStorage.removeItem("token");
          localStorage.removeItem("userData");
          localStorage.removeItem("GED_token");
          localStorage.removeItem("policyholderId");
        }, 5000);
      } else
        setTimeout(() => {
          window.location.href = PORTAL;
          localStorage.removeItem("token");
          localStorage.removeItem("userData");
          localStorage.removeItem("GED_token");
          localStorage.removeItem("policyholderId");
        }, 2000);
    } catch (error) {
      console.error("Logout failed:", error.message);
    }
  };
  const userName = localStorage.getItem("userData");

  const policyholderUserData =
    userData?.policyholderUserResponseData?.[0]?.policyHolder;

  const idleTimeout = Math.floor(idleTimeSet);
  const onIdle = async () => {
    const userid = localStorage.getItem("userId");
    setOpenSnackbar(true);

    LogoutHandler();
  };

  const { startIdleTimer } = useIdleTimer({
    onIdle: onIdle,
    timeout: idleTimeout,
    throttle: 500,
  });

  const bellIcon = (event) => {
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "notification-popover" : undefined;
  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  if (
    !HAS_PERMISSION_TO_VIEW_PAGE(
      PATH_NAME_PERMISSIONS,
      location.pathname,
      currentUserRoles
    )
  ) {
    return <></>;
  }

  return (
    <>
      <Content>
        {!policyholderPage && !hideMenu ? (
          <SidebarContainer isSidebarOpen={isSidebarOpen}>
            <Sidebar />
          </SidebarContainer>
        ) : (
          ""
        )}
        {!hideMenu && (
          <HeaderAppBar
            position="fixed"
            isSidebarOpen={isSidebarOpen}
            policyholderPage={policyholderPage}
          >
            <CustomToolbar isSidebarOpen={isSidebarOpen}>
              {/* <IconButton color="primary" onClick={toggleSidebar}>
            {isSidebarOpen ? "«" : "»"}
          </IconButton> */}
              <Button
                onClick={() =>
                  (window.location.href = !!policyholderUserData?.isApproved
                    ? HOME_PAGE
                    : POLICY_HOLDER)
                }
              >
                {policyholderPage && (
                  <Hidden smDown implementation="css">
                    <img
                      src={logo}
                      alt="Logo"
                      style={{
                        verticalAlign: "middle",
                        width: "2rem",
                        marginRight: "1rem",
                      }}
                    />
                  </Hidden>
                )}
                <Typography
                  sx={{ color: `${policyholderPage ? "#fff" : "primary"}` }}
                >
                  {t("welcome") + " " + `${userName}`}
                </Typography>
              </Button>
              <Hidden smDown implementation="css">
                <Tooltip title={"hi"}>
                  <Typography variant="caption"></Typography>
                </Tooltip>
              </Hidden>
              <Grid style={{ display: "flex", marginLeft: "auto" }}>
                <NotificationContent onClick={bellIcon}>
                  <div>
                    <NotificationCount>
                      {userData?.notificationListTotalCount}
                    </NotificationCount>
                    <NotificationsOutlinedIcon />
                  </div>
                </NotificationContent>
                <Grid style={{ marginRight: "2rem" }}>
                  <LocalizationDropdown
                    handleLanguageChange={handleLanguageChange}
                    language={language}
                  />
                </Grid>
                <Button
                  onClick={LogoutHandler}
                  startIcon={
                    <LogoutIcon
                      style={{ fill: `${policyholderPage ? "#fff" : "black"}` }}
                    />
                  }
                >
                  <Typography
                    variant="h7"
                    sx={{
                      fontWeight: "bold",
                      color: `${policyholderPage ? "#fff" : "#000"}`,
                    }}
                  >
                    {t("Logout")}
                  </Typography>
                </Button>
              </Grid>
            </CustomToolbar>
          </HeaderAppBar>
        )}
        <MainContent style={{ marginTop: hideMenu ? "0rem" : "5rem" }}>
          <Grid style={{ display: "flex", justifyContent: "center" }}>
            {!isConventionnementActive && !hideMenu && (
              <Alert
                severity="error"
                sx={{
                  width: "100%",
                  marginLeft: "1rem",
                  marginRight: "2rem",
                  marginBottom: "1rem",
                  border: "1px solid red",
                  backgroundColor: "#ffe6e6", // light red background
                  color: "red",
                }}
              >
                <StyledAlertTitle>
                  Votre conventionnement n'est pas actif
                </StyledAlertTitle>
                <Typography>
                  {"Raison"}: {blockageReason}
                </Typography>
              </Alert>
            )}
          </Grid>
          {children}
        </MainContent>
        <NotificationDialog
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          notificationData={userData?.notificationList}
          userId={userId}
        />
        <Snackbar
          open={openSnackbar}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={handleSnackbarClose} severity="warning">
            {t(" Your session has expired.")}
          </Alert>
        </Snackbar>
      </Content>
    </>
  );
};

export default Header;
